import {Box, Divider, HStack, Stack, useTheme, VStack, Button} from '@chakra-ui/react';
import React, {useEffect, useState} from 'react';
import {H3, TextG12, TextG14} from '../../../components/typography';
import {Event, Offer} from '../../../types/article';
import SwiperSlider from '../../../components/SwiperSlider';
import {filterUserTopics, updateEvent} from '../../../endpoints/api';
import EventCard from '../../../components/Articles/EventCard';
import PlaceCard from '../../../components/Articles/PlaceCard';
import {filter, map, uniqBy} from 'lodash';
import getPlaceAddress from '../../../utils/address';
import dayjs from 'dayjs';
import TopicsOfInterest from '../../TopicsOfInterest';
import useActiveRedemptions from '../../../hooks/useActiveRedemptions';
import OfferAccordion from '../../OfferAccordion';
import {useSearchParams} from 'react-router-dom';
import styled, {css} from 'styled-components';
import {parseHtmlWithComponents} from '../../../utils/htmlParserCombined';
import {eventKeywordMappings} from '../../../utils/eventKeywordMappings';
import SanDiegoInsiderWelcome from '../../../components/SanDiegoInsiderWelcome';
import useAuth from '../../../hooks/useAuth';
import {placeKeywordMappings} from "../../../utils/placeKeywordMappings";
import {InlineEditableField} from "../../../components/InlineEditableField";
import {ToastMessage} from "../../../components/toast";
import {EntityRole, useGetCurrentUserRoleForEntity} from "../../../hooks/useEntityUserRole";

const PulseAnimation = styled(Box)`
  transform: scale(1);
  filter: brightness(1);
  animation: pulse 2s infinite;

  @keyframes pulse {
    0%,
    100% {
      transform: scale(1);
      filter: brightness(1);
      -webkit-filter: brightness(1);
    }

    50% {
      transform: scale(1.05);
      filter: brightness(1.25);
      -webkit-filter: brightness(1.25);
    }
  }
`;

interface EventAboutProps {
  event: Event;
  eventOffers: Offer[];
  refetchEvent: () => void;
}

const EventAbout = ({event, eventOffers, refetchEvent}: EventAboutProps) => {
  const theme = useTheme();
  const [otherEvents, setOtherEvents] = React.useState<Event[]>([]);
  // TODO
  // const usersGoing = reverse(filter(event.event_attendance, (user) => user.pivot.status === 'Yes'));
  const usersGoing = [];
  const [searchParams] = useSearchParams();
  const {user} = useAuth();

  const {activeRedemptions} = useActiveRedemptions();

  const [offers, setOffers] = useState<Array<any>>([]);

  const hasSpecialHeader = event?.description?.includes('[[specialHeader]]') || false;
  const hasFarmersMarketHeader = event?.description?.includes('[[farmersMarketHeader]]') || false;

  const { data: currentRole } = useGetCurrentUserRoleForEntity('events', event.id!);
  const canEditEvent = currentRole?.data === EntityRole.MANAGER || currentRole?.data === EntityRole.TEAM_MEMBER;

  useEffect(() => {
    const activeOffers = map(
      filter(
        activeRedemptions,
        (redemption) =>
          redemption.offer?.event_id == event.id && !!redemption.offer.countdown_required
      ),
      (redemption) => redemption.offer
    );
    setOffers((prev) => uniqBy([...eventOffers, ...activeOffers], 'id'));
  }, [eventOffers]);

  return (
    <>
      <VStack mt={0} mb={4} px={4} spacing={4} alignItems={'start'}>
        {offers && offers.length > 0 && !hasSpecialHeader && !hasFarmersMarketHeader && (
          <>
            {searchParams.get('highlight') == 'offers' ? (
              <>
                <TextG14 fontWeight="500">Offers:</TextG14>
                <PulseAnimation w="100%">
                  <OfferAccordion offers={offers} place={event.place} event={event} />
                </PulseAnimation>
              </>
            ) : (
              <>
                <TextG14 fontWeight="500">Offers:</TextG14>
                <OfferAccordion offers={offers} place={event.place} event={event} />
              </>
            )}
          </>
        )}
        <Box w="100%">
          {!hasSpecialHeader && !hasFarmersMarketHeader && (
            <TextG14 fontWeight="500" mb={2}>
              Event details:
            </TextG14>
          )}

          <InlineEditableField
            value={event.description?.toString() || ''}
            canEdit={canEditEvent}
            isMultiline={true}
            onSave={(description) => updateEvent(event.id!, {description}).then(() => refetchEvent()).catch(e => {
              ToastMessage({
                status: 'error',
                text: e.message
              });
            })}
            renderDisplay={(value) => (
              <TextG12 className="inner-content" color={theme.colors.lightBlack}>
                {parseHtmlWithComponents(value, {
                  keywordMappings: eventKeywordMappings,
                  event: event,
                  eventOffers: eventOffers,
                })}
              </TextG12>
            )}
          />
        </Box>
        {event?.topics_of_interest && event?.topics_of_interest.length && (
          <TopicsOfInterest topicsOfInterest={event?.topics_of_interest} />
        )}
        <Divider />
        <HStack spacing={2}>
          <i className="fi fi-rr-marker"></i>
          <InlineEditableField
            value={event.place ? getPlaceAddress(event.place) : event.location}
            canEdit={canEditEvent}
            onSave={(location) => console.log('update event address to:', location)}
            renderDisplay={(value) => (
              <TextG12 fontWeight={'500'}>{value}</TextG12>
            )}
          />
        </HStack>
      </VStack>
      {event.place && (
        <Stack gap=".5rem" p="1rem" textAlign="left">
          <H3>Visit This Place</H3>
          <PlaceCard place={event.place} hideCheckin={event.allow_check_in} />
        </Stack>
      )}
      {!!otherEvents.length && !event.place && (
        <Stack gap=".5rem" p="1rem" textAlign="left">
          <H3>More events from this host</H3>
          <SwiperSlider items={otherEvents} render={(item) => <EventCard event={item} />} />
        </Stack>
      )}
    </>
  );
};

export default EventAbout;
